import { Component } from '@theme/component';
import { morph, MORPH_OPTIONS } from '@theme/morph';

/**
 * The parts of the catalog that change when a filter, the sort or the page changes. Everything
 * else (the bar's buttons, the drawer's scrim, the scripts) is left alone.
 */
const REGIONS = ['.oudh-catalog__count', '.oudh-sort', '.oudh-facets', '.oudh-catalog__results'];

/** Anything that is a plain link to another state of the same catalog: pagination and the clear buttons. */
const CATALOG_LINKS = '.oudh-pagination, .oudh-facets__clear, .oudh-facets__clear-all, .oudh-catalog__empty';

/** Theme morph options, plus: a group the shopper opened or folded keeps that state across a swap. */
const SWAP_OPTIONS = {
  ...MORPH_OPTIONS,
  /**
   * @param {Node} oldNode
   * @param {Node} newNode
   */
  onBeforeUpdate(oldNode, newNode) {
    MORPH_OPTIONS.onBeforeUpdate?.(oldNode, newNode);
    if (oldNode instanceof HTMLDetailsElement && newNode instanceof HTMLDetailsElement) {
      newNode.toggleAttribute('open', oldNode.open);
    }
  },
};

/**
 * Collection facets.
 *
 * The markup is a working GET form on its own, which is what runs without JavaScript. With it, a
 * tick, a sort, a page link or a clear button fetches the section for the new URL and swaps the
 * changed regions in place, so the page keeps its scroll position instead of reloading to the top.
 * Any failure falls back to a normal navigation. It also turns the sidebar into an off-canvas
 * drawer below the large breakpoint.
 *
 * @extends {Component<{
 *   form: HTMLFormElement,
 *   applyLabel: HTMLElement,
 *   panel: HTMLElement,
 *   scrim: HTMLElement,
 *   toggle: HTMLButtonElement,
 *   sort: HTMLSelectElement,
 * }>}
 */
class OudhFacets extends Component {
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #debounce;

  /** The layout where the facets are an off-canvas drawer (below the large breakpoint). */
  #drawerMode = window.matchMedia('(max-width: 1023px)');

  /** @type {AbortController | undefined} */
  #request;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener('keydown', this.#onKeydown);
    this.addEventListener('click', this.#onLinkClick);
    window.addEventListener('popstate', this.#onPopState);
    this.#restoreColumns();
    this.#foldGroups();
    this.#drawerMode.addEventListener('change', this.#foldGroups);
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    document.removeEventListener('keydown', this.#onKeydown);
    this.removeEventListener('click', this.#onLinkClick);
    window.removeEventListener('popstate', this.#onPopState);
    this.#drawerMode.removeEventListener('change', this.#foldGroups);
    if (this.#debounce) clearTimeout(this.#debounce);
    this.#request?.abort();
  }

  /**
   * In the drawer the groups fold, so a phone isn't one long list: the first stays open, and any
   * group with a choice in force (`data-active`) stays open so the shopper sees what is applied.
   * The desktop sidebar is always fully open. Runs on load and when the breakpoint is crossed.
   */
  #foldGroups = () => {
    const groups = this.querySelectorAll('details.oudh-facet');
    groups.forEach((group, index) => {
      if (!(group instanceof HTMLDetailsElement)) return;
      group.open = !this.#drawerMode.matches || index === 0 || group.hasAttribute('data-active');
    });
  };

  /**
   * The facet form's choices as URL params.
   *
   * Empty values are stripped so the URL carries only the facets actually in play, a price slider
   * left at either end of its range is dropped (it filters nothing, and would otherwise put
   * `price.gte=0&price.lte=110` on every URL), and `page` is never carried because a new filter
   * set invalidates the old page number.
   */
  #params() {
    const { form } = this.refs;
    const params = new URLSearchParams();
    if (!form) return params;

    /** @type {Set<string>} */
    const untouched = new Set();
    for (const range of form.querySelectorAll('input[type="range"]')) {
      if (!(range instanceof HTMLInputElement)) continue;
      const atMin = range.name.endsWith('.gte') && Number(range.value) <= Number(range.min);
      // A range max with pence (144.98) is stepped down to 144 by the browser, so compare whole units.
      const atMax = range.name.endsWith('.lte') && Number(range.value) >= Math.floor(Number(range.max));
      if (atMin || atMax) untouched.add(range.name);
    }

    for (const [key, value] of new FormData(form)) {
      if (typeof value !== 'string' || value.trim() === '' || untouched.has(key)) continue;
      params.append(key, value);
    }
    return params;
  }

  /**
   * @param {Event} [event]
   */
  submit(event) {
    const { form } = this.refs;
    if (!form) return;

    // In the off-canvas drawer a navigation per tick would reload the page under the shopper's
    // thumb, and close the drawer, after every checkbox. Ticks there only stage the choice (and
    // update the "Show N results" button); the button (`apply`) navigates once. The sort control
    // sits outside the form, so it still applies at once.
    const target = event?.target;

    // A sort pick closes its menu at once, rather than leaving it hanging open over the loading grid.
    if (target instanceof Element) {
      const menu = target.closest('details.oudh-sort');
      if (menu instanceof HTMLDetailsElement) menu.open = false;
    }

    const staged = this.#drawerMode.matches && target instanceof Node && form.contains(target);

    // Number inputs fire `change` per keystroke in some browsers; coalesce the bursts.
    if (this.#debounce) clearTimeout(this.#debounce);
    this.#debounce = setTimeout(() => (staged ? this.#preview() : this.#navigate()), staged ? 350 : 250);
  }

  /** Applies the staged filters (the drawer's "Show results" button). */
  apply() {
    this.#navigate();
  }

  #navigate() {
    const query = this.#params().toString();
    const base = window.location.pathname;
    this.#load(new URL(query ? `${base}?${query}` : base, window.location.origin));
  }

  /**
   * Plain links to another state of this catalog (page numbers, "Clear all") load in place too.
   * Modified clicks, new-tab links and anything leaving this page are left to the browser.
   *
   * @param {MouseEvent} event
   */
  #onLinkClick = (event) => {
    if (event.defaultPrevented || event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    const link = event.target instanceof Element ? event.target.closest('a[href]') : null;
    if (!(link instanceof HTMLAnchorElement) || !link.closest(CATALOG_LINKS)) return;
    if (link.target && link.target !== '_self') return;

    const url = new URL(link.href, window.location.href);
    if (url.origin !== window.location.origin || url.pathname !== window.location.pathname) return;

    event.preventDefault();
    this.#load(url, { fromLink: true });
  };

  /** Back and forward between catalog states re-render in place; other entries are the browser's. */
  #onPopState = (/** @type {PopStateEvent} */ event) => {
    if (!event.state?.oudhFacets) return;
    this.#load(new URL(window.location.href), { push: false });
  };

  /**
   * Loads a catalog state in place: fetches this section for `url` and swaps the changed regions.
   * A newer request cancels an older one, so a quick run of ticks lands on the last. Whatever goes
   * wrong, the shopper still ends up on the right page through a normal navigation.
   *
   * @param {URL} url
   * @param {{ push?: boolean, fromLink?: boolean }} [options]
   */
  async #load(url, { push = true, fromLink = false } = {}) {
    this.#request?.abort();
    const request = new AbortController();
    this.#request = request;

    const sectionUrl = new URL(url);
    sectionUrl.searchParams.set('section_id', this.id.replace('OudhCatalog-', ''));

    this.setAttribute('data-loading', '');
    this.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch(sectionUrl, { signal: request.signal });
      if (!response.ok) throw new Error(String(response.status));
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');

      if (!this.#swap(doc)) throw new Error('catalog structure changed');

      if (push) {
        // Give the entry being left a state, so Back to it is recognised as a catalog state.
        if (!window.history.state?.oudhFacets) {
          window.history.replaceState({ ...window.history.state, oudhFacets: true }, '');
        }
        window.history.pushState({ oudhFacets: true }, '', url);
      }

      // The drawer has done its job; the results are what the shopper came for.
      const wasDrawer = this.hasAttribute('data-filters-open');
      if (wasDrawer) this.closeFilters();
      if (wasDrawer || fromLink) this.#revealResults();
      if (fromLink) this.#focusResults();
    } catch (error) {
      if (request.signal.aborted) return;
      console.warn('oudh-facets: loading in place failed, navigating instead.', error);
      window.location.href = url.toString();
    } finally {
      if (this.#request === request) {
        this.#request = undefined;
        this.removeAttribute('data-loading');
        this.removeAttribute('aria-busy');
      }
    }
  }

  /**
   * Morphs the changed regions from a fetched copy of this section into the live page.
   *
   * @param {Document} doc
   * @returns {boolean} False when the two pages don't line up, so the caller should navigate.
   */
  #swap(doc) {
    const incoming = doc.getElementById(this.id);
    if (!incoming) return false;

    for (const selector of REGIONS) {
      const current = this.querySelector(selector);
      const next = incoming.querySelector(selector);
      if (!current && !next) continue;
      if (!current || !next) return false;
      morph(current, next, SWAP_OPTIONS);
    }

    // A sort menu stays open after a pick otherwise: the radios changed, the disclosure didn't.
    const sort = this.querySelector('details.oudh-sort');
    if (sort instanceof HTMLDetailsElement) sort.open = false;

    // Folded groups keep what the shopper did, but one that now holds a choice must be visible.
    for (const group of this.querySelectorAll('details.oudh-facet[data-active]')) {
      if (group instanceof HTMLDetailsElement) group.open = true;
    }
    return true;
  }

  /** Brings the top of the results back into view if the shopper has scrolled past it. */
  #revealResults() {
    const bar = this.querySelector('.oudh-catalog__bar') ?? this;
    const header = parseFloat(getComputedStyle(document.body).getPropertyValue('--header-height')) || 0;
    const offset = header + 16;
    const top = bar.getBoundingClientRect().top;
    if (top >= offset) return;

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    window.scrollTo({ top: window.scrollY + top - offset, behavior: reduced ? 'auto' : 'smooth' });
  }

  /** After a link, the link that was pressed is gone or stale, so keyboard focus moves to the count. */
  #focusResults() {
    const count = this.querySelector('.oudh-catalog__count');
    if (!(count instanceof HTMLElement)) return;
    count.setAttribute('tabindex', '-1');
    count.focus({ preventScroll: true });
  }

  /**
   * Shows how many products the staged choices would give, on the drawer's button, so ticking a
   * box visibly does something. Asks the server for just this section's HTML and reads the count
   * off it; any failure leaves the button's label as it was.
   */
  async #preview() {
    const { applyLabel } = this.refs;
    if (!applyLabel) return;

    const params = this.#params();
    params.set('section_id', this.id.replace('OudhCatalog-', ''));
    this.#previewToken = (this.#previewToken ?? 0) + 1;
    const token = this.#previewToken;

    applyLabel.textContent = 'Updating…';
    try {
      const response = await fetch(`${window.location.pathname}?${params}`);
      if (!response.ok) throw new Error(String(response.status));
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      if (token !== this.#previewToken) return;

      const total = doc.querySelector('.oudh-catalog__count strong:last-of-type')?.textContent?.trim();
      const count = total ? Number(total) : 0;
      applyLabel.textContent = count === 1 ? 'Show 1 result' : `Show ${count} results`;
    } catch {
      if (token === this.#previewToken) applyLabel.textContent = 'Show results';
    }
  }

  /** @type {number | undefined} */
  #previewToken;

  /**
   * Products per row (3 or 4) from the switch in the bar. Remembered across pages and visits.
   *
   * @param {Event} event
   */
  setColumns(event) {
    const button = event.target instanceof Element ? event.target.closest('[data-cols]') : null;
    if (!(button instanceof HTMLElement) || !button.dataset.cols) return;

    this.#applyColumns(button.dataset.cols);
    try {
      localStorage.setItem('oudh-catalog-cols', button.dataset.cols);
    } catch {
      // Private mode or blocked storage: the choice just lasts for this page.
    }
  }

  #restoreColumns() {
    try {
      const saved = localStorage.getItem('oudh-catalog-cols');
      if (saved === '3' || saved === '4') this.#applyColumns(saved);
    } catch {
      // Storage unavailable: keep the section's default.
    }
  }

  /** @param {string} cols */
  #applyColumns(cols) {
    this.setAttribute('data-cols', cols);
    for (const button of this.querySelectorAll('.oudh-density__btn')) {
      button.setAttribute('aria-pressed', String(button instanceof HTMLElement && button.dataset.cols === cols));
    }
  }

  toggleFilters() {
    if (this.hasAttribute('data-filters-open')) {
      this.closeFilters();
    } else {
      this.openFilters();
    }
  }

  openFilters() {
    const { scrim, toggle, panel } = this.refs;

    if (scrim) scrim.hidden = false;
    this.setAttribute('data-filters-open', '');
    toggle?.setAttribute('aria-expanded', 'true');
    document.body.style.overflow = 'hidden';

    const firstFocusable = panel?.querySelector('button, a[href], input:not([disabled])');
    if (firstFocusable instanceof HTMLElement) firstFocusable.focus();
  }

  closeFilters() {
    const { scrim, toggle } = this.refs;

    this.removeAttribute('data-filters-open');
    toggle?.setAttribute('aria-expanded', 'false');
    document.body.style.overflow = '';

    if (scrim) {
      const hide = () => {
        scrim.hidden = true;
      };

      // Keep the scrim until the panel has slid out. Waiting on the panel's own transition keeps the
      // two in step whatever --oudh-t-panel is set to; the timeout covers a panel with no transition
      // (reduced motion, desktop layout), where transitionend never fires.
      const { panel } = this.refs;
      const done = () => {
        clearTimeout(fallback);
        panel?.removeEventListener('transitionend', onEnd);
        hide();
      };
      /** @param {TransitionEvent} event */
      const onEnd = (event) => {
        if (event.target === panel && event.propertyName === 'transform') done();
      };
      const fallback = setTimeout(done, 600);
      panel?.addEventListener('transitionend', onEnd);
    }

    toggle?.focus();
  }

  #onKeydown = (/** @type {KeyboardEvent} */ event) => {
    if (event.key === 'Escape' && this.hasAttribute('data-filters-open')) this.closeFilters();
  };
}

if (!customElements.get('oudh-facets')) {
  customElements.define('oudh-facets', OudhFacets);
}
